import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, ShieldCheck, Sparkles, ChevronRight, ShoppingBag, PackageOpen } from "lucide-react";
import api, { imageUrl as apiImageUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import PromoPopup from "@/components/PromoPopup";
import { HeroBannerSlider } from "@/components/HeroBannerSlider";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { useStorefrontConfig } from "@/context/StorefrontConfigContext";
import { gridColsFor } from "@/lib/designTokens";
import { TrustStrip } from "@/components/storefront/TrustStrip";
import { BusinessCTASection } from "@/components/storefront/BusinessCTASection";

export function CategoryCircle({ slug, name, icon, imageUrl: catImage }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const showImage = catImage && !imgFailed;
  return (
    <Link
      to={`/katalog?category=${slug}`}
      data-testid={`category-${slug}`}
      className="storefront-category-card group"
    >
      <div className="storefront-category-icon brand-soft-bg overflow-hidden">
        {showImage ? (
          <img
            src={apiImageUrl(catImage)}
            alt={name}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="storefront-category-image"
          />
        ) : icon ? (
          <span className="text-2xl" aria-hidden="true">{icon}</span>
        ) : (
          <ShoppingBag size={22} aria-hidden="true" />
        )}
      </div>
      <span className="line-clamp-2 text-center text-xs sm:text-[13px] font-semibold leading-snug group-hover:text-[color:var(--color-accent)]">
        {name}
      </span>
    </Link>
  );
}

function ProductGridSkeleton({ gridCls, count = 4 }) {
  return (
    <div className={`grid ${gridCls} gap-3 sm:gap-5`} aria-label="Memuat produk">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="storefront-product-skeleton overflow-hidden">
          <div className="aspect-square storefront-shimmer" />
          <div className="p-3.5 sm:p-4 space-y-3">
            <div className="h-3.5 w-10/12 rounded-full storefront-shimmer" />
            <div className="h-3.5 w-7/12 rounded-full storefront-shimmer" />
            <div className="h-5 w-5/12 rounded-full storefront-shimmer" />
            <div className="h-10 w-full rounded-xl storefront-shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoriesSection({ loading, categories }) {
  return (
    <section className="storefront-section" data-testid="home-categories">
      <div className="storefront-section-heading flex items-end justify-between">
        <div>
          <div className="storefront-eyebrow brand-text-primary">Kategori</div>
          <h2 className="storefront-section-title">Pilih Kategori Favoritmu</h2>
        </div>
      </div>
      <div className="storefront-category-rail flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar pb-2">
        {loading ? Array.from({ length: 6 }).map((_, index) => (
          <div key={`category-skeleton-${index}`} className="storefront-category-card" aria-hidden="true">
            <div className="storefront-category-icon storefront-shimmer" />
            <div className="h-3 w-14 rounded-full storefront-shimmer" />
          </div>
        )) : null}
        {categories.map((c) => (
          <CategoryCircle key={c.id} slug={c.slug} name={c.name} icon={c.icon} imageUrl={c.image_url} />
        ))}
        {!loading && categories.length === 0 ? (
          <div className="storefront-empty-inline">Kategori belum tersedia.</div>
        ) : null}
      </div>
    </section>
  );
}

function ProductSection({ sec, gridCls, sliceCount }) {
  return (
    <section className="storefront-section" data-testid={`section-${sec.title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="storefront-section-heading flex items-end justify-between gap-4">
        <div>
          <div className="storefront-eyebrow brand-text-primary">{sec.subtitle}</div>
          <h2 className="storefront-section-title">{sec.title}</h2>
        </div>
        <Link to={sec.link} className="storefront-view-all brand-text-primary">
          Lihat semua <ChevronRight size={16} />
        </Link>
      </div>
      <div className={`grid ${gridCls} gap-3 sm:gap-5`}>
        {sec.items.slice(0, sliceCount).map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const { branding } = useBranding();
  const { config } = useStorefrontConfig();
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [bestseller, setBestseller] = useState([]);
  const [loading, setLoading] = useState(true);
  // Template DNA: homepage_sections → section berbasis kategori {sectionName: {items, slug}}
  const [dnaSectionItems, setDnaSectionItems] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const dnaSections = config.dna.homepage_sections || [];
  const hasDnaSections = dnaSections.length > 0;
  const gridCls = gridColsFor(config.dna.product_layout);
  const isPremium = config.design.homepage_layout === "premium-commerce";
  const sliceCount = config.dna.product_layout === "grid-3-col" ? 6 : (isPremium ? 8 : 4);
  // Tampilkan popup "after_login" hanya 1x setelah login (flag di sessionStorage)
  const [showAfterLogin, setShowAfterLogin] = useState(false);

  useEffect(() => {
    if (user && sessionStorage.getItem("show_after_login_popup") === "1") {
      setShowAfterLogin(true);
      sessionStorage.removeItem("show_after_login_popup");
    }
  }, [user]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      api.get("/banners"),
      api.get("/categories"),
      api.get("/products", { params: { featured: true, limit: 8 } }),
      api.get("/products", { params: { sort: "newest", limit: 8 } }),
      api.get("/products", { params: { bestseller: true, sort: "bestseller", limit: 8 } }),
    ]).then(([bannerRes, categoryRes, featuredRes, newestRes, bestsellerRes]) => {
      if (!active) return;
      if (bannerRes.status === "fulfilled") setBanners(bannerRes.value.data || []);
      if (categoryRes.status === "fulfilled") setCategories(categoryRes.value.data || []);
      if (featuredRes.status === "fulfilled") setFeatured(featuredRes.value.data?.items || []);
      if (newestRes.status === "fulfilled") setNewest(newestRes.value.data?.items || []);
      if (bestsellerRes.status === "fulfilled") setBestseller(bestsellerRes.value.data?.items || []);
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  // DNA homepage_sections → cocokkan ke kategori tenant, fetch produk per section.
  useEffect(() => {
    if (!hasDnaSections || categories.length === 0) return;
    dnaSections.slice(0, 6).forEach((name) => {
      const cat = categories.find(
        (c) => c.name.toLowerCase() === String(name).toLowerCase() || c.slug === String(name).toLowerCase(),
      );
      if (!cat) return;
      api.get("/products", { params: { category: cat.slug, limit: 8 } })
        .then((r) => setDnaSectionItems((s) => ({ ...s, [name]: { items: r.data.items || [], slug: cat.slug } })))
        .catch(() => {});
    });
  }, [hasDnaSections, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const dynSections = hasDnaSections
    ? dnaSections
        .map((name) => ({
          title: name,
          subtitle: `Koleksi ${name}`,
          items: dnaSectionItems[name]?.items || [],
          link: dnaSectionItems[name]?.slug ? `/katalog?category=${dnaSectionItems[name].slug}` : "/katalog",
        }))
        .filter((s) => s.items.length > 0)
    : [];
  const sections = dynSections.length > 0 ? dynSections : [
    { title: "Produk Unggulan", subtitle: "Pilihan terbaik dari toko kami", items: featured, link: "/katalog?featured=true" },
    { title: "Paling Laris", subtitle: "Favorit pelanggan kami", items: bestseller, link: "/katalog?bestseller=true" },
    { title: "Produk Terbaru", subtitle: "Baru tiba di toko kami", items: newest, link: "/katalog" },
  ];

  const popup = showAfterLogin ? <PromoPopup location="after_login" /> : <PromoPopup location="homepage" />;
  const visible = sections.filter((sec) => sec.items.length > 0);
  const emptyState = !loading && sections.every((sec) => sec.items.length === 0) ? (
    <section className="storefront-empty-state" aria-label="Produk belum tersedia">
      <span className="storefront-empty-icon system-soft-bg"><PackageOpen size={26} /></span>
      <h2 className="font-display text-xl font-bold">Produk sedang disiapkan</h2>
      <p className="text-sm text-[color:var(--brand-muted)]">Silakan kembali lagi untuk melihat koleksi terbaru dari toko ini.</p>
      <Link to="/katalog" className="system-btn-primary inline-flex h-11 items-center px-5 font-bold text-sm">Lihat Katalog</Link>
    </section>
  ) : null;

  if (isPremium) {
    // Global Commerce Premium — hierarki: BANNER → KATEGORI → PRODUK → CTA → PRODUK LAIN.
    // Data & komponen commerce sama persis; hanya susunan + gaya (CSS .gcp-*).
    return (
      <div className="gcp-home" data-homepage-layout="premium-commerce" data-testid="home-premium">
        {popup}
        <HeroBannerSlider banners={banners} layout="premium-commerce" variant="image-focus" />
        <div className="storefront-container pb-6">
          <TrustStrip />
          <CategoriesSection loading={loading} categories={categories} />
          {visible.map((sec, i) => (
            <React.Fragment key={sec.title}>
              <ProductSection sec={sec} gridCls={gridCls} sliceCount={sliceCount} />
              {i === 0 ? <BusinessCTASection /> : null}
            </React.Fragment>
          ))}
          {visible.length === 0 && !loading ? <BusinessCTASection /> : null}
          {loading ? (
            <section className="storefront-section" aria-label="Memuat produk pilihan">
              <ProductGridSkeleton gridCls={gridCls} count={sliceCount} />
            </section>
          ) : null}
          {emptyState}
        </div>
      </div>
    );
  }

  return (
    <div className="storefront-container py-4 sm:py-6 lg:py-8" data-homepage-layout={config.design.homepage_layout}>
      {/* Popup banner promo */}
      {popup}
      {/* Hero Section — Banner Slider Engine v1 (layout dari template_design) */}
      <HeroBannerSlider banners={banners} layout={config.design.homepage_layout} />

      {/* Trust badges */}
      <section className="storefront-trust-grid mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-4" aria-label="Keunggulan toko">
        {[
          { icon: Truck, title: "Pengiriman Cepat", desc: "Dikirim hari yang sama" },
          { icon: ShieldCheck, title: "Kualitas Terjamin", desc: "Produk original & terkurasi" },
          { icon: Sparkles, title: "Harga Terbaik", desc: "Langsung dari toko kami" },
        ].map((b) => {
          const Icon = b.icon;
          return (
            <div key={b.title} className="storefront-trust-card flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl brand-soft-bg grid place-items-center shrink-0">
                <Icon className="brand-text-primary" size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-[13px] sm:text-sm leading-tight">{b.title}</div>
                <div className="mt-0.5 text-[11px] sm:text-xs text-[color:var(--brand-muted)] line-clamp-2">{b.desc}</div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Categories */}
      <CategoriesSection loading={loading} categories={categories} />

      {/* Product Sections — DNA homepage_sections (kategori) atau default */}
      {sections.map((sec) => sec.items.length > 0 && (
        <ProductSection key={sec.title} sec={sec} gridCls={gridCls} sliceCount={sliceCount} />
      ))}

      {loading ? (
        <section className="storefront-section" aria-label="Memuat produk pilihan">
          <div className="storefront-section-heading">
            <div className="h-3 w-28 rounded-full storefront-shimmer" />
            <div className="mt-2 h-7 w-52 rounded-full storefront-shimmer" />
          </div>
          <ProductGridSkeleton gridCls={gridCls} count={sliceCount} />
        </section>
      ) : null}

      {emptyState}
    </div>
  );
}
